// src/common/Utils/downloadHelpers.ts

/**
 * Downloads data as a JSON file
 * @param data - The data to download (will be stringified)
 * @param filename - The name of the file (without extension)
 */
export const downloadJSON = (data: any, filename: string = 'download') => {
  try {
    // Convert data to JSON string with pretty formatting
    const jsonString = JSON.stringify(data, null, 2);

    // Create blob
    const blob = new Blob([jsonString], { type: 'application/json;charset=utf-8;' });

    // Create download link
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `${filename}.json`);

    // Trigger download
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);

    return true;
  } catch (error) {
    console.error('Failed to download JSON:', error);
    return false;
  }
};

/**
 * Downloads data as a CSV file
 * @param data - Array of objects to convert to CSV
 * @param filename - The name of the file (without extension)
 * @param columns - Optional: specify which columns to include and their order
 */
export const downloadCSV = (
  data: any[],
  filename: string = 'download',
  columns?: { [key: string]: string }, // Map of data keys to CSV headers
) => {
  try {
    if (!data || data.length === 0) {
      console.warn('No data to download');
      return false;
    }

    // Use provided columns or extract from first object
    const columnMap =
      columns ||
      Object.keys(data[0]).reduce(
        (acc, key) => {
          acc[key] = key;
          return acc;
        },
        {} as { [key: string]: string },
      );

    // Create CSV header
    const header = Object.values(columnMap).join(',');

    // Create CSV rows
    const rows = data.map((item) =>
      Object.keys(columnMap)
        .map((key) => {
          const value = item[key] ?? '';
          // Escape quotes and wrap in quotes
          return `"${String(value).replace(/"/g, '""')}"`;
        })
        .join(','),
    );

    // Combine header and rows
    const csv = [header, ...rows].join('\n');

    // Create blob
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

    // Create download link
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `${filename}.csv`);

    // Trigger download
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);

    return true;
  } catch (error) {
    console.error('Failed to download CSV:', error);
    return false;
  }
};
